import React, { useState, useEffect } from "react";

const GratuityCalculator = () => {
  const [lastSalary, setLastSalary] = useState(50000);
  const [yearsOfService, setYearsOfService] = useState(10);
  const [monthsOfService, setMonthsOfService] = useState(6);
  const [organizationType, setOrganizationType] = useState("covered");
  const [resignationType, setResignationType] = useState("retirement");
  const [result, setResult] = useState({
    lastSalary: "0.00",
    totalServiceYears: "0.00",
    workingDays: "0",
    gratuityAmount: "0.00",
    taxFreeAmount: "0.00",
    taxableAmount: "0.00",
    perYearGratuity: "0.00",
    eligibility: "",
    savingTip: "",
  });

  // Gratuity Calculation Function
  const calculateGratuity = () => {
    let salary = parseFloat(lastSalary);
    let years = parseFloat(yearsOfService);
    let months = parseFloat(monthsOfService);

    if (isNaN(salary) || salary < 0 || isNaN(years) || years < 0 || isNaN(months) || months < 0) return;

    // Total service in years
    const totalYears = years + (months / 12);
    
    // Working days (26 for covered by Gratuity Act, 30 for not covered)
    const workingDays = organizationType === "covered" ? 26 : 30;
    
    // Gratuity Formula: (Last Salary × 15 × Years of Service) / Working Days
    let gratuityAmount = (salary * 15 * totalYears) / workingDays;
    
    // Maximum gratuity limit is ₹20 lakhs (as per Payment of Gratuity Act)
    if (organizationType === "covered") {
      gratuityAmount = Math.min(gratuityAmount, 2000000);
    }
    
    // Tax exemption rules
    let taxFreeAmount = 0;
    let taxableAmount = 0;
    let eligibility = "";
    
    if (totalYears < 5 && resignationType === "voluntary") {
      gratuityAmount = 0;
      eligibility = "Not Eligible (Minimum 5 years service required for voluntary resignation)";
    } else {
      eligibility = "Eligible for Gratuity";
      
      // Tax exemption limit is ₹20 lakhs
      taxFreeAmount = Math.min(gratuityAmount, 2000000);
      taxableAmount = Math.max(0, gratuityAmount - 2000000);
    }
    
    const perYearGratuity = totalYears > 0 ? gratuityAmount / totalYears : 0;

    let savingTip = "";
    if (gratuityAmount === 0) {
      savingTip = "Complete 5 years of continuous service to become eligible for gratuity.";
    } else if (gratuityAmount < 100000) {
      savingTip = "Your gratuity is fully tax-exempt. No tax implications.";
    } else if (gratuityAmount < 500000) {
      savingTip = "Moderate gratuity amount. Entire amount is tax-free under current limits.";
    } else if (gratuityAmount < 1000000) {
      savingTip = "Good gratuity accumulation! Amount is within tax-exempt limits.";
    } else if (gratuityAmount < 2000000) {
      savingTip = "Excellent gratuity! Entire amount is tax-exempt.";
    } else {
      savingTip = "Maximum gratuity reached. Amount above ₹20L may be taxable.";
    }

    setResult({
      lastSalary: salary.toFixed(2),
      totalServiceYears: totalYears.toFixed(2),
      workingDays: workingDays.toString(),
      gratuityAmount: gratuityAmount.toFixed(2),
      taxFreeAmount: taxFreeAmount.toFixed(2),
      taxableAmount: taxableAmount.toFixed(2),
      perYearGratuity: perYearGratuity.toFixed(2),
      eligibility,
      savingTip,
    });
  };

  useEffect(() => {
    calculateGratuity();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <h1>Gratuity Calculator </h1>
          <p>
            Calculate gratuity amount as per Payment of Gratuity Act, 1972 for employees in India
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Employment Details
              </h2>

              {/* Last Drawn Salary */}
              <div className="input-group">
                <label>Last Drawn Salary (Monthly)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={lastSalary}
                    onChange={(e) => setLastSalary(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                  />
                </div>
                <div className="quick-select-group">
                  {[30000, 50000, 75000, 100000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setLastSalary(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Years of Service */}
              <div className="input-group">
                <label>Years of Service</label>
                <div className="input-wrapper">
                
                  <input
                    type="number"
                    value={yearsOfService}
                    onChange={(e) => setYearsOfService(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                  />
                </div>
                <div className="quick-select-group">
                  {[5, 10, 15, 20].map((years) => (
                    <button
                      key={years}
                      onClick={() => setYearsOfService(years)}
                      className="quick-select-btn success"
                    >
                      {years} Years
                    </button>
                  ))}
                </div>
              </div>

              {/* Months of Service */}
              <div className="input-group">
                <label>Additional Months</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={monthsOfService}
                    onChange={(e) => setMonthsOfService(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    max="11"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 3, 6, 9].map((months) => (
                    <button
                      key={months}
                      onClick={() => setMonthsOfService(months)}
                      className="quick-select-btn secondary"
                    >
                      {months} Months
                    </button>
                  ))}
                </div>
              </div>

              {/* Organization Type */}
              <div className="input-group">
                <label>Organization Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "covered", label: "Covered by Act (26 days)" },
                    { value: "notcovered", label: "Not Covered (30 days)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setOrganizationType(type.value)}
                      className={`quick-select-btn ${organizationType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Resignation Type */}
              <div className="input-group">
                <label>Reason for Leaving</label>
                <div className="quick-select-group">
                  {[
                    { value: "retirement", label: "Retirement" },
                    { value: "voluntary", label: "Resignation" },
                    { value: "death", label: "Death/Disability" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setResignationType(type.value)}
                      className={`quick-select-btn ${resignationType === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateGratuity} className="calculate-btn">
                Calculate Gratuity
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Gratuity Calculation
              </h2>

              <div className="result-card">
                {/* Gratuity Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Gratuity Amount</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.gratuityAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.eligibility}
                  </div>
                </div>

                {/* Tax Free Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Tax-Free Amount</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.taxFreeAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Exempt from income tax
                  </div>
                </div>

                {/* Taxable Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Taxable Amount</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.taxableAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Subject to income tax
                  </div>
                </div>

                {/* Service Period */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Service Period</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.totalServiceYears} Years
                  </div>
                  <div className="result-subtext">
                    Completed service duration
                  </div>
                </div>

                {/* Per Year Gratuity */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Gratuity Per Year</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.perYearGratuity).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Average per year of service
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Gratuity Calculation Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Last Drawn Salary</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.lastSalary).toLocaleString("en-IN")} /month
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Service Years × 15 Days</span>
                      <span className="breakdown-percentage">
                        {result.totalServiceYears} years × 15 days
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.totalServiceYears) / 30) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Working Days in Month</span>
                      <span className="breakdown-percentage">
                        {result.workingDays} days
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill purple"
                        style={{
                          width: `${(parseFloat(result.workingDays) / 30) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Tax Distribution */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Tax Implications</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Tax-Free Portion</span>
                      <span className="breakdown-percentage">
                        {parseFloat(result.gratuityAmount) > 0 
                          ? ((parseFloat(result.taxFreeAmount) / parseFloat(result.gratuityAmount)) * 100).toFixed(2)
                          : 0}% (₹{Number(result.taxFreeAmount).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseFloat(result.gratuityAmount) > 0 
                            ? ((parseFloat(result.taxFreeAmount) / parseFloat(result.gratuityAmount)) * 100)
                            : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Taxable Portion</span>
                      <span className="breakdown-percentage">
                        {parseFloat(result.gratuityAmount) > 0 
                          ? ((parseFloat(result.taxableAmount) / parseFloat(result.gratuityAmount)) * 100).toFixed(2)
                          : 0}% (₹{Number(result.taxableAmount).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseFloat(result.gratuityAmount) > 0 
                            ? ((parseFloat(result.taxableAmount) / parseFloat(result.gratuityAmount)) * 100)
                            : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Saving Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Gratuity Info</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.savingTip} Gratuity up to ₹20 lakh is tax-free under Income Tax Act Section 10(10).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Gratuity in India</h3>
              <p>
                Gratuity is a lump sum payment made by an employer to an employee for services rendered to the 
                organization. As per the Payment of Gratuity Act, 1972, gratuity is payable to employees who have 
                completed at least 5 years of continuous service. The Act covers all establishments employing 10 or 
                more employees. For organizations covered under the Act, gratuity is calculated based on 26 working 
                days per month, while for others it's 30 days. The formula is: (Last Salary × 15 × Years of Service) ÷ 
                Working Days. Maximum gratuity payable is capped at ₹20 lakhs. Gratuity is payable on retirement, 
                resignation (after 5 years), death, or disablement. It must be paid within 30 days of becoming due. 
                Gratuity up to ₹20 lakhs is exempt from income tax under Section 10(10) of Income Tax Act.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Gratuity Calculation Formula:</h4>
            <div className="formula-display">
              Gratuity = (Last Salary × 15 × Years of Service) ÷ Working Days per Month
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Last Salary</div>
                <div className="formula-param-value">Basic salary + DA of last drawn month</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">15 Days</div>
                <div className="formula-param-value">Fixed multiplier as per Gratuity Act</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Working Days</div>
                <div className="formula-param-value">26 days (covered by Act) or 30 days (not covered)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Maximum Limit</div>
                <div className="formula-param-value">₹20 lakhs as per current law</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is the minimum service required to get gratuity?</h5>
              <p>
                Minimum 5 years of continuous service is required. Exception: in case of death or disablement, no minimum service required.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is included in salary for gratuity calculation?</h5>
              <p>
                Last drawn basic salary plus dearness allowance (DA). Other allowances, bonuses, and commissions are not included.
              </p>
            </div>

            <div className="faq-item">
              <h5>Is gratuity taxable?</h5>
              <p>
                Gratuity up to ₹20 lakhs is tax-free under Section 10(10). Any amount exceeding ₹20 lakhs is taxable as per income tax slab.
              </p>
            </div>

            <div className="faq-item">
              <h5>When should gratuity be paid?</h5>
              <p>
                Employer must pay gratuity within 30 days from the date it becomes payable. Delay attracts penalty and interest.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can employer refuse to pay gratuity?</h5>
              <p>
                No, gratuity is a statutory right. Employer can forfeit only in case of termination for misconduct causing loss to employer.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if I change jobs frequently?</h5>
              <p>
                Gratuity is calculated for each employer separately. You must complete 5 years with each employer to claim gratuity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GratuityCalculator;